import { useDecision } from '@optimizely/react-sdk';
import { useMemo } from 'react';
import { Categories } from '@pizza-hut-us-development/client-core';
import { useCCGetProducts } from '@/clientCore/temporaryTransformationalHooks/useCCGetProducts';
import { LocalizedMenuPageSections } from '@/common/components/LocalizedProductTile/LocalizedProductTile.types';
import ProductId from '@/common/ProductId';
import { DisplayableMenuItem } from '@/domain/product/types';

type ProductOrderVariables = {
  product_order: {
    globalIds: string[];
  };
};

const sortProducts = (data: DisplayableMenuItem[], productOrder: string[]): DisplayableMenuItem[] => {
  const productsWithoutDisplayOrder = data?.filter((product) => !product?.displayOrder) ?? [];
  const productsWithDisplayOrder = data?.filter((product) => product?.displayOrder) ?? [];

  productsWithDisplayOrder?.sort((productA, productB) => {
    if (productA?.displayOrder && productB?.displayOrder) {
      if (productA.displayOrder > productB.displayOrder) return 1;
      if (productA.displayOrder < productB.displayOrder) return -1;

      return productA.name.localeCompare(productB.name);
    }

    return productA?.name.localeCompare(productB?.name);
  });

  const products = [...productsWithDisplayOrder, ...productsWithoutDisplayOrder];

  const orderedProducts = productOrder.reduce((acc, value) => {
    const productIndex = products.findIndex((product) => new ProductId(product.id).globalId === value);
    if (productIndex > -1) {
      const [foundProduct] = products.splice(productIndex, 1);
      acc.push(foundProduct);
    }
    return acc;
  }, [] as DisplayableMenuItem[]);

  return [...orderedProducts, ...products];
};

interface MenuPageData {
  loading: boolean;
  error: string;
  products: DisplayableMenuItem[];
  subCategories: { heading: string; products: DisplayableMenuItem[] }[];
}

export const useGetMenuPageData = (categoryID: string): MenuPageData => {
  const isLineup = categoryID === Categories.LINEUP;
  const [{
    enabled: productOrderDecisionEnabled,
    variables: productOrderDecisionVars
  }] = useDecision('fr-web-1507-vlu-product-order');
  const [{ enabled: isUsingYumAdminSorting }] = useDecision('dtg-1376-match_the_order_what_is_in_yum_admin_for_category_1f8sd');
  const {
    data: ccData,
    loading: ccLoading,
    error: ccError
  } = useCCGetProducts({
    // TODO: Remove this toLowerCase once CC puts in their fix CCS-1729
    categoryId: Categories[categoryID.toUpperCase() as keyof typeof Categories]?.toLowerCase() as Categories,
    skip: categoryID === Categories.PIZZA
  });
  const data = (ccData) as LocalizedMenuPageSections;

  const productOrder = useMemo(
    () => (
      isLineup && productOrderDecisionEnabled
        ? (productOrderDecisionVars as ProductOrderVariables).product_order
        : { globalIds: [] }
    ),
    [productOrderDecisionEnabled, productOrderDecisionVars, isLineup]
  );

  const products: DisplayableMenuItem[] = useMemo(
    () => {
      if (!data) return [];
      return isUsingYumAdminSorting 
        ? data.productsForMainCategory 
        : sortProducts(data.productsForMainCategory, productOrder.globalIds);
    },
    [data, isUsingYumAdminSorting, productOrder.globalIds]
  );

  const subCategories = useMemo(() => {
    if (data && data.subCategories) {
      return data.subCategories.map((s) => ({
        heading: s.heading,
        products: sortProducts(s.products as DisplayableMenuItem[], productOrder.globalIds)
      }));
    }
    return [];
  }, [data, productOrder.globalIds]);

  return {
    loading: !!ccLoading,
    error: ccError as string,
    products,
    subCategories
  };
};
